import React, {FC, ReactNode, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {globalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {HttpResponseError} from '../../../../libs/shared/core/src/services/http/httpError';
import {boxedInputColorReaders} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {DialogButtonGroup} from '../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {CardGroupModel} from '../../../models/api/cardGroupModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {NamespaceDropdownBoxContainer} from '../../common/dropdowns/namespace/namespaceDropdownBoxContainer';

/*
 * Constants.
 */

const defaultDropdownWidth = 260;
const accessDropdownHeight = 300;

/*
 * Props.
 */

export interface OnConfirmOptions {
  name: string;
  namespace: string;
}

interface CardGroupPopoverProps {
  popoverProps: PopoverTransferProps;
  userNamespace: string;
  cardGroup?: CardGroupModel;
  defaultNamespace?: string;
  namespaceDisplayMessage?: ReactNode;
  isNamespaceDropdownDisabled?: boolean;
  dropdownWidth?: number;
  onDelete?: () => void;
  onConfirm: (options: OnConfirmOptions) => Promise<void>;
  onClose: (arg1: boolean, model?: CardGroupModel) => void;
}

/*
 * Styles.
 */

const StyledGenericErrorDiv = styled.div`
  text-align: center;
  color: ${boxedInputColorReaders.textError};
  margin-top: 8px;
  font-size: ${fontSizes.verySmall};
`;

/*
 * Component.
 */

export const CardGroupPopover: FC<CardGroupPopoverProps> = ({
  popoverProps,
  userNamespace,
  cardGroup,
  isNamespaceDropdownDisabled = false,
  namespaceDisplayMessage,
  defaultNamespace,
  dropdownWidth = defaultDropdownWidth,
  onDelete,
  onConfirm,
  onClose,
}) => {
  const [name, setName] = useState(cardGroup?.name || '');
  const [namespace, setNamespace] = useState(defaultNamespace || globalNamespace);
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<ReactNode>(undefined);
  const [genericError, setGenericError] = useState<ReactNode>(undefined);

  const isConfirmButtonDisabled = !name || isSaving || cardGroup?.name === name;

  const onConfirmClick = useCallback(async () => {
    // Clear any existing errors
    setNameError(undefined);
    setGenericError(undefined);
    setIsSaving(true);

    try {
      await onConfirm({name, namespace});
    } catch (error) {
      if (error instanceof HttpResponseError && error.status === 409) {
        setNameError(renderConflictErrorMessage());
      } else {
        setGenericError(
          <Trans id="contact-group-dropdown-generic-error">An unexpected error occurred</Trans>,
        );
      }
    }
    setIsSaving(false);
  }, [name, namespace, onConfirm, setNameError, setGenericError, setIsSaving]);

  const onCancel = () => {
    // Prevent closing while saving
    if (isSaving) {
      return;
    }
    onClose(false);
  };
  return (
    <Dropdown {...popoverProps} width={dropdownWidth} onRequestClose={onCancel} hasBackground>
      <DropdownHeader title={renderDropdownHeaderTitle(cardGroup)} />
      <DialogContent>
        <TextBox
          label={<Trans id="contact-group-dropdown-name">Name</Trans>}
          value={name}
          hasError={Boolean(nameError)}
          errorMessage={nameError}
          onChange={setName}
          shouldAutoFocus
          isRequired
          interactionId="contact-group-dropdown-name-input"
        />
        <NamespaceDropdownBoxContainer
          placeholder={renderNamespaceVisibilityText()}
          resourceNamespace={userNamespace}
          selectedNamespace={namespace}
          onNamespaceSelect={setNamespace}
          permission={PermissionsEnum.EDIT_CARD_GROUP}
          isDisplayOnly={cardGroup ? true : isNamespaceDropdownDisabled}
          messageForDisplayOnly={namespaceDisplayMessage}
          header={<Trans id="contact-group-dropdown-access-header">Access</Trans>}
          maxHeight={accessDropdownHeight}
          shouldRenderSharedDropdownItem
          shouldRenderVisibilityDescription
          isRequired
        />
        {genericError && <StyledGenericErrorDiv>{genericError}</StyledGenericErrorDiv>}
      </DialogContent>
      <DialogButtonGroup>
        {cardGroup && (
          <SameWidthButton
            variant={ButtonVariantsEnum.SECONDARY_DANGER}
            onClick={onDelete}
            isDisabled={isSaving}
            interactionId="contact-group-dropdown-delete-btn"
          >
            <Trans id="contact-group-dropdown-delete-button">Delete</Trans>
          </SameWidthButton>
        )}
        <ButtonGroupSpacer />
        <SameWidthButton
          onClick={onCancel}
          isDisabled={isSaving}
          interactionId="contact-group-dropdown-cancel-btn"
        >
          <Trans id="contact-group-dropdown-cancel-button">Cancel</Trans>
        </SameWidthButton>
        <SameWidthButton
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={isConfirmButtonDisabled}
          onClick={onConfirmClick}
          isRounded
          interactionId="contact-group-dropdown-confirm-btn"
        >
          {renderConfirmButton(cardGroup)}
        </SameWidthButton>
      </DialogButtonGroup>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderNamespaceVisibilityText() {
  return (
    <Trans id="contact-group-dropdown-access-placeholder">Select the visibility of the contact list</Trans>
  );
}

function renderConflictErrorMessage() {
  return <Trans id="contact-group-dropdown-error-conflict">This list already exists</Trans>;
}

function renderConfirmButton(cardGroup?: CardGroupModel) {
  if (cardGroup) {
    return <Trans id="contact-group-dropdown-save-button">Save</Trans>;
  }
  return <Trans id="contact-group-dropdown-create-button">Create</Trans>;
}

function renderDropdownHeaderTitle(cardGroup?: CardGroupModel) {
  if (cardGroup) {
    return <Trans id="contact-group-dropdown-header-edit">Edit list</Trans>;
  }
  return <Trans id="contact-group-dropdown-header-create">Create list</Trans>;
}
